import React, { useState, useEffect } from "react";
import { Checkbox, Tag } from "antd"; // Import Checkbox and Tag
import { ClockCircleOutlined } from "@ant-design/icons"; // Import icon
import {
  fetchPharmacies,
  fetchPharmaciesByLocation,
  fetchLocalPharmacies, // Import fetchLocalPharmacies
} from "../../api/pharmacy";
import PharmacyMap from "../PharmacyMap";
import PharmacyCard from "./PharmacyCard"; // Import PharmacyCard
import "./PharmacyLocator.css";
// const BASE_URL = process.env.REACT_APP_API_BASE_URL; // Not used here directly
const PharmacyLocator = () => {
  const [query, setQuery] = useState("Hyderabad");
  const [results, setResults] = useState([]); // For Google Maps results
  const [localResults, setLocalResults] = useState([]); // For registered pharmacies
  const [filterOpen247, setFilterOpen247] = useState(false); // State for the 24/7 filter

  // Combined fetch function to avoid code duplication
  const performSearch = async (currentQuery, is247Filtered) => {
    try {
      const searchLocation = currentQuery || "pharmacy"; // Default if query is empty
      // Fetch local pharmacies
      const localData = await fetchLocalPharmacies(
        searchLocation,
        is247Filtered
      );
      setLocalResults(localData);

      // Fetch pharmacies from Google
      const googleData = await fetchPharmacies(searchLocation, is247Filtered);
      if (googleData?.results) {
        setResults(googleData.results);
      } else {
        setResults([]); // Clear previous Google results if new search yields none
      }
    } catch (error) {
      console.error("Search failed:", error);
      // Optionally, clear results or show error message to user
      setLocalResults([]);
      setResults([]);
    }
  };

  useEffect(() => {
    // Initial fetch on component mount
    performSearch(query, filterOpen247);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOpen247]); // Re-fetch if filterOpen247 changes

  const handleSearch = () => {
    performSearch(query, filterOpen247);
  };

  // handleLocationSearch primarily fetches from Google's nearby search,
  // which doesn't currently have the 24/7 filter parameter in our backend.
  // If we want to filter these results too, it would need to be client-side
  // based on opening_hours from Google's response.
  // For now, this function remains unchanged as per plan.
  const handleLocationSearch = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const data = await fetchPharmaciesByLocation(latitude, longitude);
          if (data?.results) setResults(data.results);
        },
        (error) => {
          alert("Location access denied or unavailable.");
          console.error("Geolocation error:", error);
        }
      );
    } else {
      alert("Geolocation not supported by your browser.");
    }
  };

  // Helper function to display Google Maps opening hours
  const getGoogleOpeningHoursText = (openingHours) => {
    if (!openingHours) {
      return null; // No data
    }
    if (openingHours.permanently_closed) {
      return <Tag color="red">Permanently Closed</Tag>;
    }
    // Check for "always open" / 24-7 indication
    // Google often signifies 24/7 by having a single period object for the whole week without a close time.
    // Or sometimes, a specific text might be available, or open_now is true and periods suggest it.
    // This is a simplified check; a more robust one would parse periods array.
    if (
      openingHours.periods &&
      openingHours.periods.length === 1 &&
      openingHours.periods[0].open &&
      !openingHours.periods[0].close
    ) {
      return (
        <Tag color="green">
          <ClockCircleOutlined /> Open 24 hours
        </Tag>
      );
    }

    if (typeof openingHours.open_now !== "undefined") {
      return openingHours.open_now ? (
        <Tag color="green">
          <ClockCircleOutlined /> Open now
        </Tag>
      ) : (
        <Tag color="volcano">
          <ClockCircleOutlined /> Closed
        </Tag>
      );
    }

    // Fallback if open_now is not present but weekday_text might be (less common for direct API)
    if (openingHours.weekday_text) {
      // weekday_text is an array, you might want to show today's hours
      // For simplicity, just indicate that hours are available
      return (
        <Tag>
          <ClockCircleOutlined /> See opening hours
        </Tag>
      );
    }
    return null; // Default if no useful info found
  };

  return (
    <div className="pharmacy-container">
      <div className="pharmacy-search">
        <h2>Find Nearby Pharmacies</h2>
        <input
          type="text"
          placeholder="Search by Pincode, Address, Locality or Town"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="search-input"
        />
        <div className="search-buttons">
          <button onClick={handleSearch} className="small-button">
            Search
          </button>
          <button onClick={handleLocationSearch} className="small-button">
            📍 Use My Location
          </button>
        </div>
        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
          <Checkbox
            checked={filterOpen247}
            onChange={(e) => setFilterOpen247(e.target.checked)}
          >
            Show only 24/7 open pharmacies
          </Checkbox>
        </div>

        {localResults.length > 0 && (
          <div className="local-pharmacies-results">
            <h3
              style={{
                marginTop: "1rem",
                marginBottom: "1rem",
                paddingLeft: "5px",
              }}
            >
              Registered Pharmacies
            </h3>
            {localResults.map((pharmacy) => (
              <PharmacyCard key={pharmacy.id} pharmacy={pharmacy} />
            ))}
          </div>
        )}

        {results.length > 0 && (
          <>
            <h3 style={{ marginTop: "1rem" }}>
              Pharmacies from non-registered Users
            </h3>
            <ul>
              {results.map((place, index) => (
                <li key={index} style={{ marginBottom: "0.75rem" }}>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(
                      place.name
                    )}&query_place_id=${place.place_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "underline", color: "#1a73e8" }}
                  >
                    <strong>{place.name}</strong>
                  </a>
                  {getGoogleOpeningHoursText(place.opening_hours) && (
                    <span style={{ marginLeft: "10px" }}>
                      {getGoogleOpeningHoursText(place.opening_hours)}
                    </span>
                  )}
                  <br />
                  {place.formatted_address || place.vicinity}
                  {/* Optional: More detailed hours if available and needed */}
                  {/* {place.opening_hours && place.opening_hours.weekday_text && (
                    <ul style={{ fontSize: '0.8em', listStyleType: 'none', paddingLeft: '5px' }}>
                      {place.opening_hours.weekday_text.map((line, i) => <li key={i}>{line}</li>)}
                    </ul>
                  )} */}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="pharmacy-map">
        <PharmacyMap
          pharmacies={[
            ...results,
            ...localResults
              .filter((p) => p.latitude && p.longitude)
              .map((pharmacy) => ({
                name: pharmacy.name,
                address: pharmacy.address,
                geometry: {
                  location: {
                    lat: pharmacy.latitude,
                    lng: pharmacy.longitude,
                  },
                },
              })),
          ]}
        />
      </div>
    </div>
  );
};

export default PharmacyLocator;
